// components/CompactRestaurantCard.tsx
"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { BadgeCheck, MapPin, Star, Award } from "lucide-react";

import type { Vendor } from "@/lib/types/vendor";
import { getOptimizedImageUrl } from "@/lib/utils/images.media";
import ImageOptimizer from "../media/ImageOptimizer";

interface CompactRestaurantCardProps {
  vendor: Vendor;
  index: number;
  isFavorite?: boolean;
  onToggleFavorite?: (e: React.MouseEvent, id: string) => void;
  hideFavorite?: boolean;
  className?: string;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function titleCase(input: string) {
  if (!input) return "";
  return input
    .replace(/[_-]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function clampRating(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(5, Math.max(0, n));
}

function mapPriceRange(range?: string | null) {
  const r = String(range || "").toLowerCase().trim();
  if (!r) return "";
  if (["low", "cheap", "budget", "basic", "affordable"].includes(r)) return "Affordable";
  if (["medium", "mid", "standard", "average", "moderate"].includes(r)) return "Moderate";
  if (["high", "premium", "expensive", "luxury", "upmarket"].includes(r)) return "Premium";
  return "Moderate";
}

function getPriceChip(range?: string | null) {
  const r = String(range || "").toLowerCase().trim();
  if (["low", "cheap", "budget", "basic", "affordable"].includes(r))
    return "bg-emerald-500/10 text-slate-900 border-emerald-500/20";
  if (["medium", "mid", "standard", "average", "moderate"].includes(r))
    return "bg-amber-400/15 text-slate-900 border-amber-400/25";
  if (["high", "premium", "expensive", "luxury", "upmarket"].includes(r))
    return "bg-orange-500/10 text-slate-900 border-orange-500/20";
  return "bg-black/5 text-slate-900 border-black/10";
}

function statusPill(status?: string) {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "open") return { text: "Open", className: "bg-emerald-600 text-white border-white/15" };
  if (s === "pending" || s === "opening soon")
    return { text: "Opening Soon", className: "bg-orange-500 text-white border-white/15" };
  if (s === "inactive" || s === "closed") return { text: "Closed", className: "bg-black/60 text-white border-white/15" };
  if (s === "suspended") return { text: "Closed", className: "bg-red-500 text-white border-white/15" };
  return { text: "Open", className: "bg-emerald-600 text-white border-white/15" };
}

function shimmer(width: number, height: number) {
  return `
<svg width="${width}" height="${height}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#F5F5F7" offset="20%" />
      <stop stop-color="#EAEAEE" offset="50%" />
      <stop stop-color="#F5F5F7" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${width}" height="${height}" fill="#F5F5F7" />
  <rect id="r" width="${width}" height="${height}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${width}" to="${width}" dur="1.05s" repeatCount="indefinite" />
</svg>`;
}

function toBase64(str: string) {
  if (typeof window === "undefined") return Buffer.from(str).toString("base64");
  return window.btoa(str);
}

export default function CompactRestaurantCard({ vendor, index, className = "" }: CompactRestaurantCardProps) {
  const [logoError, setLogoError] = useState(false);

  const rating = clampRating(vendor?.detail?.average_rating);
  const reviews = Number(vendor?.detail?.total_reviews || 0) || 0;
  const hasRating = rating > 0 && reviews > 0;

  const cuisineLabel = useMemo(() => {
    const list = vendor?.detail?.cuisine_types;
    if (!Array.isArray(list) || list.length === 0) return "Restaurant";
    return list.map((c) => titleCase(String(c))).join(" • ");
  }, [vendor?.detail?.cuisine_types]);

  const locationLabel = useMemo(() => {
    const city = vendor?.address?.city ? String(vendor.address.city) : "";
    const area = vendor?.address?.street ? String(vendor.address.street) : "";
    if (city && area) return `${area}, ${city}`;
    if (city) return city;
    if (area) return area;
    return "Location not specified";
  }, [vendor?.address?.city, vendor?.address?.street]);

  const priceLabel = useMemo(() => mapPriceRange(vendor?.detail?.price_range), [vendor?.detail?.price_range]);
  const priceChip = useMemo(() => getPriceChip(vendor?.detail?.price_range), [vendor?.detail?.price_range]);
  const status = useMemo(() => statusPill(vendor?.status), [vendor?.status]);

  const href = `/vendors/${vendor?.id}/`;

  const coverImageUrl = useMemo(() => {
    const raw = vendor?.detail?.cover_url;
    if (!raw) return "";
    try {
      return getOptimizedImageUrl(raw, "md", "cover", { fallback: false });
    } catch {
      return raw;
    }
  }, [vendor?.detail?.cover_url]);

  const logoImageUrl = useMemo(() => {
    const raw = vendor?.detail?.logo_url;
    if (!raw) return "";
    try {
      return getOptimizedImageUrl(raw, "sm", "logo", { fallback: false });
    } catch {
      return raw;
    }
  }, [vendor?.detail?.logo_url]);

  const shimmerBase64 = `data:image/svg+xml;base64,${toBase64(shimmer(1400, 1000))}`;

  return (
    <Link
      href={href}
      prefetch={false}
      className={cx(
        "group block w-full outline-none",
        "focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
        className
      )}
      aria-label={`${vendor?.name || "Restaurant"} - ${cuisineLabel}`}
      data-testid={`restaurant-card-${index}`}
    >
      <motion.article
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: Math.min(0.25, index * 0.04) }}
        whileHover={{ y: -4 }}
        className={cx(
          "relative w-full overflow-hidden rounded-2xl",
          "bg-white/75 backdrop-blur-xl",
          "border border-white/55",
          "shadow-[0_10px_35px_-22px_rgba(0,0,0,0.35)]",
          "transition-all duration-300 hover:shadow-[0_18px_60px_-30px_rgba(0,0,0,0.45)]",
          "max-w-[460px] mx-auto"
        )}
      >
        {/* Cover */}
        <div className="relative w-full overflow-hidden bg-black/5">
          <div className="relative h-[230px] sm:h-[245px] md:h-[260px] w-full">
            {coverImageUrl ? (
              <ImageOptimizer
                src={coverImageUrl}
                alt={`${vendor?.name || "Restaurant"} cover image`}
                fill
                quality={96}
                priority={index < 8}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 460px"
                placeholder="blur"
                blurDataURL={shimmerBase64}
                className={cx("object-cover transition-transform duration-700 ease-out", "group-hover:scale-[1.03]")}
                showErrorState
                showLoadingState
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/15 via-black/5 to-orange-500/10" />
            )}

            {/* Minimal shading for readability (keeps photo visible) */}
            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/18 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/20 to-transparent" />

            {/* Status */}
            <div className="absolute left-4 top-4 z-20">
              <span
                className={cx(
                  "inline-flex items-center rounded-full px-3 py-1.5 text-[11px] font-semibold",
                  "border shadow-sm backdrop-blur-md",
                  status.className
                )}
              >
                {status.text}
              </span>
            </div>

            {/* Top-right Logo */}
            <div className="absolute right-4 top-4 z-30">
              <div
                className={cx(
                  "h-10 w-10 rounded-full",
                  "bg-white/80 backdrop-blur-md",
                  "border border-white/70",
                  "shadow-[0_12px_34px_-22px_rgba(0,0,0,0.6)]",
                  "overflow-hidden"
                )}
                aria-label={`${vendor?.name || "Restaurant"} logo`}
              >
                {logoImageUrl && !logoError ? (
                  <ImageOptimizer
                    src={logoImageUrl}
                    alt={`${vendor?.name || "Restaurant"} logo`}
                    width={40}
                    height={40}
                    sizes="40px"
                    quality={92}
                    className="h-full w-full object-contain p-1.5"
                    onError={() => setLogoError(true)}
                    showErrorState={false}
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center">
                    <span className="text-[12px] font-semibold text-slate-900">{vendor?.name?.charAt(0) || "R"}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Featured */}
            {vendor?.is_featured && (
              <div className="absolute right-4 top-16 z-20">
                <span
                  className={cx(
                    "inline-flex items-center gap-2 rounded-full px-3 py-2",
                    "bg-white/75 backdrop-blur-md",
                    "border border-white/60",
                    "shadow-[0_10px_30px_-18px_rgba(0,0,0,0.55)]"
                  )}
                >
                  <Award className="h-4 w-4 text-orange-500" />
                  <span className="text-[11px] font-semibold text-slate-900">Featured</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content (overlaps image a bit to remove that “white gap”) */}
        <div
          className={cx(
            "relative z-10",
            "-mt-6", // ✅ key fix: pulls content up, eliminates the visible empty gap
            "rounded-t-2xl",
            "bg-white/85 backdrop-blur-xl",
            "border-t border-white/70",
            "px-4 pb-4 pt-4"
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="min-w-0 truncate text-[18px] font-semibold tracking-tight text-slate-900">
                  {vendor?.name || "Restaurant"}
                </h3>

                {/* Verified (blue) */}
                {vendor?.is_verified && (
                  <span
                    className={cx(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
                      "bg-blue-600 text-white",
                      "border border-blue-500/40 shadow-sm"
                    )}
                    aria-label="Verified restaurant"
                    title="Verified"
                  >
                    <BadgeCheck className="h-4 w-4 text-white" />
                    <span className="text-[11px] font-semibold">Verified</span>
                  </span>
                )}
              </div>

              <p className="mt-1 text-[13px] text-slate-500 truncate">{cuisineLabel}</p>
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              {/* Rating */}
              <span
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5",
                  "bg-black/5 text-slate-900",
                  "border border-black/10"
                )}
                aria-label={hasRating ? `Rating ${rating.toFixed(1)} out of 5` : "New restaurant"}
              >
                <Star className={cx("h-4 w-4", hasRating ? "text-orange-500 fill-orange-500" : "text-slate-600")} />
                <span className="text-[12px] font-semibold">{hasRating ? rating.toFixed(1) : "New"}</span>
                {hasRating && <span className="text-[11px] text-slate-500">({reviews.toLocaleString()})</span>}
              </span>

              {/* Price */}
              {priceLabel ? (
                <span className={cx("inline-flex items-center rounded-full px-3 py-1.5 text-[12px] font-semibold border", priceChip)}>
                  {priceLabel}
                </span>
              ) : null}
            </div>
          </div>

          {/* Description */}
          {vendor?.description ? (
            <p className="mt-2 text-[13px] leading-relaxed text-slate-700 line-clamp-2">{vendor.description}</p>
          ) : null}

          {/* Location (below description) */}
          <div className="mt-2 flex items-center gap-2 text-[13px] text-slate-500">
            <MapPin className="h-4 w-4 shrink-0 text-slate-700/70" />
            <span className="truncate">{locationLabel}</span>
          </div>

          {/* Footer (tightened spacing to avoid bottom emptiness) */}
          <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
            <div className="text-[12px] font-medium text-slate-500">
              <span className="text-slate-900 font-semibold">MWK</span>
              <span className="mx-2">•</span>
              <span>{vendor?.detail?.price_range ? titleCase(String(vendor.detail.price_range)) : "Moderate"}</span>
            </div>

            <div
              className={cx(
                "inline-flex items-center gap-2 text-[13px] font-semibold",
                "text-slate-900",
                "transition-colors duration-200 group-hover:text-orange-600"
              )}
              aria-hidden="true"
            >
              <span>View Menu</span>
              <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
            </div>
          </div>
        </div>

        {/* Hover ring */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-blue-500/12 transition-colors duration-300"
          aria-hidden="true"
        />
      </motion.article>
    </Link>
  );
}
